/**
 * Security Agent Dashboard
 *
 * Provides visibility into the autonomous security agent:
 * - Live activity feed (WebSocket)
 * - Pending approvals with approve/deny
 * - Threat detection history
 * - Remediation log
 * - Learning mode validation
 * - Rules status and promotion
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card as BaseCard, Button, Badge, Alert as SharedAlert, Loader } from './shared';

// Simple wrapper components for missing shared components
const LoadingSpinner = () => <Loader message="Loading..." />;
const EmptyState = ({ message }) => (
  <div className="text-center py-8 text-gray-500">{message}</div>
);

// Alert wrapper that supports children (shared Alert uses message prop)
const Alert = ({ type = 'info', children, className = '' }) => {
  const colors = {
    error: 'bg-red-50 border-red-200 text-red-800',
    warning: 'bg-yellow-50 border-yellow-200 text-yellow-800',
    info: 'bg-blue-50 border-blue-200 text-blue-800',
    success: 'bg-green-50 border-green-200 text-green-800',
  };
  return (
    <div className={`border rounded-lg p-4 ${colors[type] || colors.info} ${className}`}>
      {children}
    </div>
  );
};

// Enhanced Card with title support
const Card = ({ title, headerAction, children, className = '' }) => (
  <BaseCard className={className}>
    {title && (
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
        <h3 className="font-semibold text-gray-900">{title}</h3>
        {headerAction}
      </div>
    )}
    <div className={title ? 'p-4' : ''}>{children}</div>
  </BaseCard>
);

// API base URL for security agent
// Use proxy path for HTTPS, direct connection for local development
const useProxy = typeof window !== 'undefined' && window.location.protocol === 'https:';
const SECURITY_AGENT_API = useProxy ? '/api/security-agent' : 'http://localhost:8020';

// Severity color mapping
const SEVERITY_COLORS = {
  critical: 'bg-red-600 text-white',
  high: 'bg-orange-500 text-white',
  medium: 'bg-yellow-500 text-black',
  low: 'bg-blue-500 text-white',
};

const SEVERITY_BADGE = {
  critical: 'red',
  high: 'orange',
  medium: 'yellow',
  low: 'blue',
};

// Status colors
const STATUS_COLORS = {
  detected: 'bg-yellow-100 text-yellow-800',
  contained: 'bg-blue-100 text-blue-800',
  remediated: 'bg-green-100 text-green-800',
  resolved: 'bg-gray-100 text-gray-800',
  false_positive: 'bg-purple-100 text-purple-800',
};

// Rule status colors
const RULE_STATUS_COLORS = {
  learning: 'bg-purple-100 text-purple-800',
  monitored: 'bg-blue-100 text-blue-800',
  autonomous: 'bg-green-100 text-green-800',
  disabled: 'bg-gray-100 text-gray-800',
};

// ===========================================
// API Functions
// ===========================================

async function fetchAPI(endpoint, options = {}) {
  const response = await fetch(`${SECURITY_AGENT_API}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });
  if (!response.ok) {
    throw new Error(`API error: ${response.status}`);
  }
  return response.json();
}

// ===========================================
// Sub-components
// ===========================================

function SeverityBadge({ severity }) {
  const color = SEVERITY_BADGE[severity] || 'gray';
  return (
    <span className={`px-2 py-1 text-xs font-medium rounded-full ${SEVERITY_COLORS[severity] || 'bg-gray-500 text-white'}`}>
      {severity?.toUpperCase()}
    </span>
  );
}

function StatusBadge({ status }) {
  return (
    <span className={`px-2 py-1 text-xs font-medium rounded-full ${STATUS_COLORS[status] || 'bg-gray-100 text-gray-800'}`}>
      {status?.replace('_', ' ')}
    </span>
  );
}

function RuleStatusBadge({ status }) {
  return (
    <span className={`px-2 py-1 text-xs font-medium rounded-full ${RULE_STATUS_COLORS[status] || 'bg-gray-100 text-gray-800'}`}>
      {status}
    </span>
  );
}

function TimeAgo({ timestamp }) {
  const [, setTick] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => setTick(t => t + 1), 60000);
    return () => clearInterval(interval);
  }, []);

  if (!timestamp) return <span className="text-gray-400">-</span>;

  const date = new Date(timestamp);
  const now = new Date();
  const diff = Math.floor((now - date) / 1000);

  if (diff < 60) return <span className="text-gray-500">just now</span>;
  if (diff < 3600) return <span className="text-gray-500">{Math.floor(diff / 60)}m ago</span>;
  if (diff < 86400) return <span className="text-gray-500">{Math.floor(diff / 3600)}h ago</span>;
  return <span className="text-gray-500">{Math.floor(diff / 86400)}d ago</span>;
}

// ===========================================
// Live Activity Feed (WebSocket)
// ===========================================

function LiveActivityFeed() {
  const [events, setEvents] = useState([]);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState(null);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  const connect = useCallback(() => {
    try {
      const wsUrl = SECURITY_AGENT_API.replace('http', 'ws') + '/api/activity/stream';
      wsRef.current = new WebSocket(wsUrl);

      wsRef.current.onopen = () => {
        setConnected(true);
        setError(null);
      };

      wsRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          setEvents(prev => [{ ...data, id: Date.now() }, ...prev].slice(0, 50));
        } catch (e) {
          console.error('Failed to parse WebSocket message:', e);
        }
      };

      wsRef.current.onclose = () => {
        setConnected(false);
        reconnectTimeoutRef.current = setTimeout(connect, 5000);
      };

      wsRef.current.onerror = () => {
        setError('WebSocket connection failed');
        setConnected(false);
      };
    } catch (e) {
      setError('Failed to connect to activity stream');
    }
  }, []);

  useEffect(() => {
    connect();
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [connect]);

  return (
    <Card title="Live Activity Feed" className="h-96">
      <div className="flex items-center gap-2 mb-3">
        <div className={`w-2 h-2 rounded-full ${connected ? 'bg-green-500' : 'bg-red-500'}`} />
        <span className="text-sm text-gray-500">
          {connected ? 'Connected' : 'Disconnected'}
        </span>
      </div>

      {error && (
        <Alert type="warning" className="mb-3">
          {error} - Using polling fallback
        </Alert>
      )}

      <div className="space-y-2 overflow-y-auto max-h-72">
        {events.length === 0 ? (
          <p className="text-gray-400 text-center py-8">Waiting for events...</p>
        ) : (
          events.map(event => (
            <div key={event.id} className="flex items-start gap-3 p-2 bg-gray-50 rounded text-sm">
              <div className="flex-shrink-0">
                {event.type === 'event_received' && (
                  <span className="text-blue-500">&#x25CF;</span>
                )}
                {event.type === 'threat_detected' && (
                  <span className="text-red-500">&#x26A0;</span>
                )}
                {event.type === 'action_executed' && (
                  <span className="text-green-500">&#x2713;</span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{event.event_type || event.type}</p>
                <p className="text-gray-500 text-xs">
                  {event.server_id && `Server: ${event.server_id}`}
                </p>
              </div>
              <TimeAgo timestamp={event.timestamp} />
            </div>
          ))
        )}
      </div>
    </Card>
  );
}

// ===========================================
// Overview Dashboard
// ===========================================

function OverviewTab() {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchOverview = useCallback(async () => {
    try {
      const data = await fetchAPI('/api/dashboard/overview');
      setOverview(data);
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOverview();
    const interval = setInterval(fetchOverview, 30000);
    return () => clearInterval(interval);
  }, [fetchOverview]);

  if (loading) return <LoadingSpinner />;
  if (error) return <Alert type="error">Failed to load overview: {error}</Alert>;

  const stats = [
    { label: 'Agent Status', value: overview?.agent_status || 'unknown', color: overview?.agent_status === 'running' ? 'text-green-600' : 'text-red-600' },
    { label: 'Operational Mode', value: overview?.operational_mode || '-', color: 'text-blue-600' },
    { label: 'Threats (24h)', value: overview?.threats_24h || 0, color: 'text-orange-600' },
    { label: 'Remediations (24h)', value: overview?.remediations_24h || 0, color: 'text-green-600' },
    { label: 'Pending Approvals', value: overview?.pending_approvals || 0, color: overview?.pending_approvals > 0 ? 'text-red-600' : 'text-gray-600' },
    { label: 'Active Collectors', value: overview?.active_collectors || 0, color: 'text-blue-600' },
  ];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div className="lg:col-span-2">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
          {stats.map(stat => (
            <Card key={stat.label} className="text-center">
              <p className="text-gray-500 text-sm">{stat.label}</p>
              <p className={`text-2xl font-bold ${stat.color}`}>{stat.value}</p>
            </Card>
          ))}
        </div>
        <PendingApprovalsCard />
      </div>
      <div className="lg:col-span-1">
        <LiveActivityFeed />
      </div>
    </div>
  );
}

// ===========================================
// Pending Approvals
// ===========================================

function PendingApprovalsCard() {
  const [approvals, setApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  const fetchApprovals = useCallback(async () => {
    try {
      const data = await fetchAPI('/api/approvals/pending');
      setApprovals(data.approvals || []);
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchApprovals();
    const interval = setInterval(fetchApprovals, 15000);
    return () => clearInterval(interval);
  }, [fetchApprovals]);

  const handleAction = async (approvalId, action) => {
    setActionLoading(approvalId);
    try {
      await fetchAPI(`/api/approvals/${approvalId}/${action}`, {
        method: 'POST',
        body: JSON.stringify({ approver: 'dashboard_user' }),
      });
      await fetchApprovals();
    } catch (e) {
      alert(`Failed to ${action}: ${e.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) return <Card title="Pending Approvals"><LoadingSpinner /></Card>;
  if (error) return <Card title="Pending Approvals"><Alert type="error">{error}</Alert></Card>;

  return (
    <Card
      title="Pending Approvals"
      headerAction={
        approvals.length > 0 && (
          <Badge color="red">{approvals.length}</Badge>
        )
      }
    >
      {approvals.length === 0 ? (
        <EmptyState message="No pending approvals" />
      ) : (
        <div className="space-y-3">
          {approvals.slice(0, 5).map(approval => (
            <div key={approval.id} className="p-3 border rounded-lg bg-gray-50">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <SeverityBadge severity={approval.severity} />
                  <span className="ml-2 font-medium">{approval.rule_name}</span>
                </div>
                <TimeAgo timestamp={approval.created_at} />
              </div>
              <p className="text-sm text-gray-600 mb-2">{approval.description}</p>
              <p className="text-xs text-gray-500 mb-3">Server: {approval.server_id}</p>

              {approval.expires_at && (
                <p className="text-xs text-orange-600 mb-2">
                  Expires: {new Date(approval.expires_at).toLocaleString()}
                </p>
              )}

              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="primary"
                  onClick={() => handleAction(approval.id, 'approve')}
                  disabled={actionLoading === approval.id}
                >
                  {actionLoading === approval.id ? '...' : 'Approve'}
                </Button>
                <Button
                  size="sm"
                  variant="danger"
                  onClick={() => handleAction(approval.id, 'deny')}
                  disabled={actionLoading === approval.id}
                >
                  Deny
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

// ===========================================
// Threats Tab
// ===========================================

function ThreatsTab() {
  const [threats, setThreats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({ severity: '', status: '' });

  const fetchThreats = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filters.severity) params.append('severity', filters.severity);
      if (filters.status) params.append('status', filters.status);
      params.append('limit', '50');

      const data = await fetchAPI(`/api/threats?${params}`);
      setThreats(data.threats || []);
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchThreats();
  }, [fetchThreats]);

  if (loading) return <LoadingSpinner />;
  if (error) return <Alert type="error">Failed to load threats: {error}</Alert>;

  return (
    <div>
      <div className="flex gap-4 mb-4">
        <select
          value={filters.severity}
          onChange={(e) => setFilters(f => ({ ...f, severity: e.target.value }))}
          className="px-3 py-2 border rounded-lg text-sm"
        >
          <option value="">All Severities</option>
          <option value="critical">Critical</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>

        <select
          value={filters.status}
          onChange={(e) => setFilters(f => ({ ...f, status: e.target.value }))}
          className="px-3 py-2 border rounded-lg text-sm"
        >
          <option value="">All Statuses</option>
          <option value="detected">Detected</option>
          <option value="contained">Contained</option>
          <option value="remediated">Remediated</option>
          <option value="resolved">Resolved</option>
          <option value="false_positive">False Positive</option>
        </select>

        <Button size="sm" onClick={fetchThreats}>Refresh</Button>
      </div>

      <Card>
        {threats.length === 0 ? (
          <EmptyState message="No threats found" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-3">Timestamp</th>
                  <th className="text-left p-3">Severity</th>
                  <th className="text-left p-3">Status</th>
                  <th className="text-left p-3">Rule</th>
                  <th className="text-left p-3">Server</th>
                  <th className="text-left p-3">Description</th>
                </tr>
              </thead>
              <tbody>
                {threats.map(threat => (
                  <tr key={threat.id} className="border-b hover:bg-gray-50">
                    <td className="p-3">
                      <TimeAgo timestamp={threat.timestamp} />
                    </td>
                    <td className="p-3">
                      <SeverityBadge severity={threat.severity} />
                    </td>
                    <td className="p-3">
                      <StatusBadge status={threat.status} />
                    </td>
                    <td className="p-3 font-mono text-xs">{threat.rule_name}</td>
                    <td className="p-3">{threat.server_id}</td>
                    <td className="p-3 max-w-xs truncate">{threat.description}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

// ===========================================
// Approvals Tab (Full View)
// ===========================================

function ApprovalsTab() {
  const [approvals, setApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  const fetchApprovals = useCallback(async () => {
    try {
      const data = await fetchAPI('/api/approvals/pending');
      setApprovals(data.approvals || []);
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchApprovals();
    const interval = setInterval(fetchApprovals, 10000);
    return () => clearInterval(interval);
  }, [fetchApprovals]);

  const handleAction = async (approvalId, action, reason = '') => {
    setActionLoading(approvalId);
    try {
      await fetchAPI(`/api/approvals/${approvalId}/${action}`, {
        method: 'POST',
        body: JSON.stringify({ approver: 'dashboard_user', reason }),
      });
      await fetchApprovals();
    } catch (e) {
      alert(`Failed to ${action}: ${e.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) return <LoadingSpinner />;
  if (error) return <Alert type="error">Failed to load approvals: {error}</Alert>;

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold">Pending Approvals ({approvals.length})</h3>
        <Button size="sm" onClick={fetchApprovals}>Refresh</Button>
      </div>

      {approvals.length === 0 ? (
        <Card>
          <EmptyState message="No pending approvals" />
        </Card>
      ) : (
        <div className="space-y-4">
          {approvals.map(approval => (
            <Card key={approval.id} className={`border-l-4 ${
              approval.severity === 'critical' ? 'border-l-red-600' :
              approval.severity === 'high' ? 'border-l-orange-500' :
              approval.severity === 'medium' ? 'border-l-yellow-500' : 'border-l-blue-500'
            }`}>
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <SeverityBadge severity={approval.severity} />
                    <span className="font-semibold">{approval.rule_name}</span>
                    <span className="text-gray-500">|</span>
                    <span className="text-sm text-gray-600">{approval.server_id}</span>
                  </div>

                  <p className="text-gray-700 mb-3">{approval.description}</p>

                  <div className="text-sm text-gray-500 space-y-1">
                    <p>Threat ID: <code className="bg-gray-100 px-1 rounded">{approval.threat_id}</code></p>
                    <p>Created: {new Date(approval.created_at).toLocaleString()}</p>
                    {approval.expires_at && (
                      <p className="text-orange-600">
                        Expires: {new Date(approval.expires_at).toLocaleString()}
                      </p>
                    )}
                  </div>

                  <div className="mt-3 p-3 bg-gray-50 rounded">
                    <p className="text-sm font-medium mb-2">Proposed Actions:</p>
                    <ul className="text-sm space-y-1">
                      {approval.proposed_actions?.containment && (
                        <li className="text-blue-700">
                          <span className="font-medium">Containment:</span> {approval.proposed_actions.containment}
                        </li>
                      )}
                      {approval.proposed_actions?.remediation && (
                        <li className="text-green-700">
                          <span className="font-medium">Remediation:</span> {approval.proposed_actions.remediation}
                        </li>
                      )}
                    </ul>
                  </div>
                </div>

                <div className="flex lg:flex-col gap-2">
                  <Button
                    variant="primary"
                    onClick={() => handleAction(approval.id, 'approve')}
                    disabled={actionLoading === approval.id}
                    className="flex-1 lg:flex-none"
                  >
                    {actionLoading === approval.id ? 'Processing...' : 'Approve'}
                  </Button>
                  <Button
                    variant="danger"
                    onClick={() => handleAction(approval.id, 'deny')}
                    disabled={actionLoading === approval.id}
                    className="flex-1 lg:flex-none"
                  >
                    Deny
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ===========================================
// Remediations Tab
// ===========================================

function RemediationsTab() {
  const [remediations, setRemediations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchRemediations = useCallback(async () => {
    try {
      const data = await fetchAPI('/api/remediations?limit=100');
      setRemediations(data.remediations || []);
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRemediations();
  }, [fetchRemediations]);

  if (loading) return <LoadingSpinner />;
  if (error) return <Alert type="error">Failed to load remediations: {error}</Alert>;

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold">Remediation Log</h3>
        <Button size="sm" onClick={fetchRemediations}>Refresh</Button>
      </div>

      <Card>
        {remediations.length === 0 ? (
          <EmptyState message="No remediations recorded" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-3">Time</th>
                  <th className="text-left p-3">Type</th>
                  <th className="text-left p-3">Action</th>
                  <th className="text-left p-3">Server</th>
                  <th className="text-left p-3">Status</th>
                  <th className="text-left p-3">Executed By</th>
                </tr>
              </thead>
              <tbody>
                {remediations.map(rem => (
                  <tr key={rem.id} className="border-b hover:bg-gray-50">
                    <td className="p-3">
                      <TimeAgo timestamp={rem.executed_at} />
                    </td>
                    <td className="p-3">
                      <span className={`px-2 py-1 text-xs rounded ${
                        rem.action_type === 'containment' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
                      }`}>
                        {rem.action_type}
                      </span>
                    </td>
                    <td className="p-3 font-mono text-xs">{rem.action_name}</td>
                    <td className="p-3">{rem.server_id}</td>
                    <td className="p-3">
                      <span className={`px-2 py-1 text-xs rounded ${
                        rem.status === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {rem.status}
                      </span>
                    </td>
                    <td className="p-3">{rem.executed_by}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

// ===========================================
// Learning Mode Tab
// ===========================================

function LearningTab() {
  const [predictions, setPredictions] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [validating, setValidating] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [predictionsData, candidatesData] = await Promise.all([
        fetchAPI('/api/learning/predictions?limit=50'),
        fetchAPI('/api/learning/promotion-candidates'),
      ]);
      setPredictions(predictionsData.predictions || []);
      setCandidates(candidatesData.candidates || []);
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleValidate = async (predictionId, wasCorrect, falsePositive = false) => {
    setValidating(predictionId);
    try {
      await fetchAPI(`/api/learning/predictions/${predictionId}/validate`, {
        method: 'POST',
        body: JSON.stringify({
          was_correct: wasCorrect,
          validated_by: 'dashboard_user',
          false_positive: falsePositive,
        }),
      });
      await fetchData();
    } catch (e) {
      alert(`Failed to validate: ${e.message}`);
    } finally {
      setValidating(null);
    }
  };

  const handlePromote = async (ruleName, toStatus) => {
    try {
      await fetchAPI(`/api/rules/${ruleName}/promote`, {
        method: 'POST',
        body: JSON.stringify({
          to_status: toStatus,
          promoted_by: 'dashboard_user',
        }),
      });
      await fetchData();
      alert(`Rule "${ruleName}" promoted to ${toStatus}`);
    } catch (e) {
      alert(`Failed to promote: ${e.message}`);
    }
  };

  if (loading) return <LoadingSpinner />;
  if (error) return <Alert type="error">Failed to load learning data: {error}</Alert>;

  return (
    <div className="space-y-6">
      <Card title="Promotion Candidates">
        {candidates.length === 0 ? (
          <EmptyState message="No rules ready for promotion" />
        ) : (
          <div className="space-y-3">
            {candidates.map(candidate => (
              <div key={candidate.rule_name} className="p-3 border rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <span className="font-medium">{candidate.rule_name}</span>
                    <RuleStatusBadge status={candidate.current_status} />
                    <span className="text-gray-400">&rarr;</span>
                    <RuleStatusBadge status={candidate.target_status} />
                  </div>
                  {candidate.ready && (
                    <Button
                      size="sm"
                      variant="primary"
                      onClick={() => handlePromote(candidate.rule_name, candidate.target_status)}
                    >
                      Promote
                    </Button>
                  )}
                </div>
                <div className="text-sm text-gray-600">
                  <span>Accuracy: {(candidate.stats?.accuracy * 100 || 0).toFixed(1)}%</span>
                  <span className="mx-2">|</span>
                  <span>Validated: {candidate.stats?.validated || 0}</span>
                  <span className="mx-2">|</span>
                  <span>FP Rate: {(candidate.stats?.false_positive_rate * 100 || 0).toFixed(1)}%</span>
                </div>
                {!candidate.ready && candidate.reasons && (
                  <div className="mt-2 text-sm text-orange-600">
                    Not ready: {candidate.reasons.join(', ')}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card title="Predictions Awaiting Validation">
        {predictions.length === 0 ? (
          <EmptyState message="No predictions need validation" />
        ) : (
          <div className="space-y-3">
            {predictions.map(pred => (
              <div key={pred.id} className="p-3 border rounded-lg bg-gray-50">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <span className="font-medium">{pred.rule_name}</span>
                    <span className="mx-2">|</span>
                    <SeverityBadge severity={pred.predicted_severity} />
                  </div>
                  <TimeAgo timestamp={pred.created_at} />
                </div>
                <p className="text-sm text-gray-600 mb-2">
                  Event: {pred.event_type} on {pred.server_id}
                </p>
                <p className="text-sm text-gray-500 mb-3">
                  Would have done: {JSON.stringify(pred.predicted_actions)}
                </p>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="primary"
                    onClick={() => handleValidate(pred.id, true)}
                    disabled={validating === pred.id}
                  >
                    Correct
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => handleValidate(pred.id, false)}
                    disabled={validating === pred.id}
                  >
                    Incorrect
                  </Button>
                  <Button
                    size="sm"
                    variant="danger"
                    onClick={() => handleValidate(pred.id, false, true)}
                    disabled={validating === pred.id}
                  >
                    False Positive
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

// ===========================================
// Rules Tab
// ===========================================

function RulesTab() {
  const [rules, setRules] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchRules = useCallback(async () => {
    try {
      const data = await fetchAPI('/api/rules');
      setRules(data);
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRules();
  }, [fetchRules]);

  if (loading) return <LoadingSpinner />;
  if (error) return <Alert type="error">Failed to load rules: {error}</Alert>;

  const rulesByStatus = {
    autonomous: [],
    monitored: [],
    learning: [],
    disabled: [],
  };

  (rules?.rules || []).forEach(rule => {
    const status = rule.status || 'learning';
    if (rulesByStatus[status]) {
      rulesByStatus[status].push(rule);
    }
  });

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold">Rules Engine</h3>
        <div className="text-sm text-gray-500">
          Total: {rules?.total || 0} rules
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {Object.entries(rulesByStatus).map(([status, statusRules]) => (
          <Card key={status} className="text-center">
            <RuleStatusBadge status={status} />
            <p className="text-2xl font-bold mt-2">{statusRules.length}</p>
          </Card>
        ))}
      </div>

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left p-3">Rule Name</th>
                <th className="text-left p-3">Status</th>
                <th className="text-left p-3">Severity</th>
                <th className="text-left p-3">Matches</th>
              </tr>
            </thead>
            <tbody>
              {(rules?.rules || []).map(rule => (
                <tr key={rule.name} className="border-b hover:bg-gray-50">
                  <td className="p-3 font-mono text-xs">{rule.name}</td>
                  <td className="p-3">
                    <RuleStatusBadge status={rule.status} />
                  </td>
                  <td className="p-3">
                    <SeverityBadge severity={rule.severity} />
                  </td>
                  <td className="p-3">{rule.matches || 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

// ===========================================
// Main Component
// ===========================================

export default function SecurityAgentDashboard() {
  const [activeTab, setActiveTab] = useState('overview');
  const [healthStatus, setHealthStatus] = useState(null);

  useEffect(() => {
    const checkHealth = async () => {
      try {
        const data = await fetchAPI('/health');
        setHealthStatus(data);
      } catch (e) {
        setHealthStatus({ status: 'unhealthy', error: e.message });
      }
    };
    checkHealth();
    const interval = setInterval(checkHealth, 60000);
    return () => clearInterval(interval);
  }, []);

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'threats', label: 'Threats' },
    { id: 'approvals', label: 'Approvals' },
    { id: 'remediations', label: 'Remediations' },
    { id: 'learning', label: 'Learning Mode' },
    { id: 'rules', label: 'Rules' },
  ];

  const renderTab = () => {
    switch (activeTab) {
      case 'overview': return <OverviewTab />;
      case 'threats': return <ThreatsTab />;
      case 'approvals': return <ApprovalsTab />;
      case 'remediations': return <RemediationsTab />;
      case 'learning': return <LearningTab />;
      case 'rules': return <RulesTab />;
      default: return <OverviewTab />;
    }
  };

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">Security Agent</h1>
          <p className="text-gray-500">Autonomous threat detection and response</p>
        </div>
        <div className="flex items-center gap-3">
          {healthStatus && (
            <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm ${
              healthStatus.status === 'healthy'
                ? 'bg-green-100 text-green-800'
                : 'bg-red-100 text-red-800'
            }`}>
              <div className={`w-2 h-2 rounded-full ${
                healthStatus.status === 'healthy' ? 'bg-green-500' : 'bg-red-500'
              }`} />
              {healthStatus.status === 'healthy' ? 'Connected' : 'Disconnected'}
            </div>
          )}
          {healthStatus?.mode && (
            <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
              Mode: {healthStatus.mode}
            </span>
          )}
        </div>
      </div>

      {healthStatus?.status !== 'healthy' && (
        <Alert type="error" className="mb-4">
          Cannot connect to Security Agent at {SECURITY_AGENT_API}.
          Make sure the agent is running.
        </Alert>
      )}

      <div className="border-b mb-6">
        <nav className="flex gap-4">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {renderTab()}
    </div>
  );
}
